import * as polyline from '@googlemaps/polyline-codec'
import { GOOGLE_MAP_API_KEY } from 'config'
import GoogleMapReact from 'google-map-react'
import { useEffect, useMemo, useState } from 'react'
import { getAddressRoutes } from 'services'
import { toHumanizeDistance, toHumanizeDuration } from 'utils'

import { Toggle } from '../Toggle/Toggle'
import { Tooltip } from '../Tooltip/Tooltip'

// Different colors for each route pair
const ROUTE_COLORS = ['#0077ff', '#ff5722', '#4caf50', '#9c27b0', '#ff9800', '#e91e63', '#00bcd4', '#795548']

interface RouteData {
  origin: string
  destination: string
  path: { lat: number; lng: number }[]
  distance: string
  duration: string
  color: string
}

function Marker({ text, label, color }: any) {
  return (
    <Tooltip message={text}>
      <div
        style={{
          position: 'relative',
          transform: 'translate(-50%, -100%)',
          display: 'inline-block',
        }}
      >
        {label && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              backgroundColor: color || '#0077ff',
              color: 'white',
              borderRadius: '50%',
              width: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '12px',
              fontWeight: 'bold',
              border: '2px solid white',
              boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            {label}
          </div>
        )}
      </div>
    </Tooltip>
  )
}

// Generate all unique pairs from addresses array
function generatePairs(addresses: string[]): [string, string][] {
  const pairs: [string, string][] = []
  for (let i = 0; i < addresses.length; i++) {
    for (let j = i + 1; j < addresses.length; j++) {
      pairs.push([addresses[i], addresses[j]])
    }
  }
  return pairs
}

export function GoogleMapWithRoutes({ addresses, setLoading }: { addresses: string[]; setLoading: Function }) {
  const [routes, setRoutes] = useState<RouteData[]>([])
  const [visibleRoutes, setVisibleRoutes] = useState<boolean[]>([])
  const [mapInstance, setMapInstance] = useState<any>(null)
  const [mapsApi, setMapsApi] = useState<any>(null)
  const [polylines, setPolylines] = useState<any[]>([])

  const pairs = useMemo(() => generatePairs(addresses), [addresses])

  useEffect(() => {
    if (addresses.length < 2) return

    setLoading(true)
    setRoutes([])

    const fetchAllRoutes = async () => {
      const routePromises = pairs.map(([origin, destination], index) =>
        getAddressRoutes(origin, destination)
          .then((res) => {
            const route = res[0]
            const decodedPath = polyline.decode(route.polyline.encodedPolyline).map(([lat, lng]) => ({ lat, lng }))
            return {
              origin,
              destination,
              path: decodedPath,
              distance: toHumanizeDistance(route.distanceMeters),
              duration: toHumanizeDuration(route.duration),
              color: ROUTE_COLORS[index % ROUTE_COLORS.length],
            } as RouteData
          })
          .catch(() => null),
      )

      const results = await Promise.all(routePromises)
      const filteredResults = results.filter((r): r is RouteData => r !== null)
      setRoutes(filteredResults)
      setVisibleRoutes(new Array(filteredResults.length).fill(true))
      setLoading(false)
    }

    fetchAllRoutes()
  }, [pairs, setLoading])

  const onMapLoad = ({ map, maps }: any) => {
    setMapInstance(map)
    setMapsApi(maps)
  }

  useEffect(() => {
    if (!mapInstance || !mapsApi || !routes.length) return

    polylines.forEach((polyline) => polyline.setMap(null))

    const newPolylines = routes
      .map((route, index) => {
        if (visibleRoutes[index]) {
          return new mapsApi.Polyline({
            path: route.path,
            map: mapInstance,
            strokeColor: route.color,
            strokeWeight: 4,
            strokeOpacity: 0.8,
          })
        }
        return null
      })
      .filter((p) => p !== null)

    setPolylines(newPolylines)

    if (polylines.length === 0 && visibleRoutes.some((v) => v)) {
      const bounds = new mapsApi.LatLngBounds()
      routes.forEach((route, index) => {
        if (visibleRoutes[index]) {
          route.path.forEach((point) => {
            bounds.extend(point)
          })
        }
      })
      mapInstance.fitBounds(bounds)
    }
  }, [mapInstance, mapsApi, routes, visibleRoutes])

  const markers = useMemo(() => {
    const markerList: { address: string; lat: number; lng: number; label: string; color: string }[] = []
    routes.forEach((route, index) => {
      if (visibleRoutes[index]) {
        if (route.path.length > 0) {
          markerList.push({
            address: route.origin,
            lat: route.path[0].lat,
            lng: route.path[0].lng,
            label: 'S',
            color: route.color,
          })
          const lastPoint = route.path[route.path.length - 1]
          markerList.push({
            address: route.destination,
            lat: lastPoint.lat,
            lng: lastPoint.lng,
            label: 'E',
            color: route.color,
          })
        }
      }
    })
    return markerList
  }, [routes, visibleRoutes])

  return (
    <div className="flex flex-row gap-2 w-full h-full min-h-[300px]">
      {/* Route information for all pairs */}
      {routes.length > 0 && (
        <div className="w-120 h-full flex flex-col gap-3 overflow-y-auto">
          {routes.map((route, index) => (
            <div
              key={index}
              className="flex flex-col gap-1 p-2 border rounded"
              style={{ borderLeftColor: route.color, borderLeftWidth: 4 }}
            >
              <div className="flex items-center justify-between mb-1">
                <strong style={{ color: route.color }}>Route {index + 1}</strong>
                <Toggle
                  id={`route-toggle-${index}`}
                  value={visibleRoutes[index]}
                  onChange={(checked) => {
                    const newVisibleRoutes = [...visibleRoutes]
                    newVisibleRoutes[index] = checked
                    setVisibleRoutes(newVisibleRoutes)
                  }}
                />
              </div>
              {[
                ['From', route.origin],
                ['To', route.destination],
                ['Distance', route.distance],
                ['Duration', route.duration],
              ].map(([label, value]) => (
                <p key={label}>
                  <strong>{label}:</strong> {value}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}
      {/* Map view container */}
      {routes.length > 0 && (
        <div className="w-196">
          <GoogleMapReact
            options={{
              mapTypeId: 'roadmap',
              mapTypeControl: true,
              mapTypeControlOptions: {
                mapTypeIds: ['roadmap', 'hybrid'],
              },
            }}
            yesIWantToUseGoogleMapApiInternals
            bootstrapURLKeys={{ key: GOOGLE_MAP_API_KEY! }}
            defaultCenter={routes[0].path[0]}
            defaultZoom={7}
            onGoogleApiLoaded={onMapLoad}
          >
            {markers.map((marker, index) => (
              <Marker
                key={index}
                lat={marker.lat}
                lng={marker.lng}
                text={marker.address}
                label={marker.label}
                color={marker.color}
              />
            ))}
          </GoogleMapReact>
        </div>
      )}
    </div>
  )
}
